import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, Eye, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { productionLogger } from "@/utils/production-logger";

interface SessionStats {
  activeUsers: number;
  totalSessions: number;
  averageSessionTime: string;
  mobileUsers: number;
  lastUpdated: string;
}

export function SessionStatsWidget() {
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchSessionStats = async () => {
      try {
        // Enhanced Security: Check rate limiting before proceeding
        const { data: rateLimitCheck, error: rateLimitError } = await supabase
          .rpc('check_admin_dashboard_rate_limit');
          
        if (rateLimitError) {
          throw new Error(`Rate limit check failed: ${rateLimitError.message}`);
        }
        
        if (!rateLimitCheck) {
          throw new Error('Rate limit exceeded for session stats widget access');
        }

        // Enhanced Security: Log widget access for audit trail
        const { error: logError } = await supabase
          .rpc('log_dashboard_widget_access', {
            p_widget_type: 'session_stats',
            p_data_points: 1
          });
          
        if (logError) {
          productionLogger.warn('Failed to log session widget access:', logError);
          // Continue despite logging failure - don't block widget functionality
        }

        // Query active user sessions
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('user_sessions')
          .select('*')
          .eq('is_active', true)
          .gte('last_seen', new Date(Date.now() - 30 * 60 * 1000).toISOString()); // Active in last 30 minutes

        if (sessionsError) {
          throw sessionsError;
        }

        // Query total sessions from today
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        
        const { data: totalSessionsData, error: totalError } = await supabase
          .from('user_sessions')
          .select('user_agent, started_at, last_seen')
          .gte('started_at', todayStart.toISOString());

        if (totalError) {
          throw totalError;
        }

        if (mounted) {
          const activeSessions = sessionsData || [];
          const totalSessions = totalSessionsData || [];
          
          // Calculate mobile users (simple user agent check)
          const mobileCount = activeSessions.filter(session => 
            session.user_agent?.toLowerCase().includes('mobile') ||
            session.user_agent?.toLowerCase().includes('android') ||
            session.user_agent?.toLowerCase().includes('iphone')
          ).length;

          // Calculate average session time
          const sessionTimes = totalSessions
            .filter(session => session.last_seen && session.started_at)
            .map(session => {
              const start = new Date(session.started_at).getTime();
              const end = new Date(session.last_seen).getTime();
              return Math.max(0, end - start);
            });
          
          const avgTime = sessionTimes.length > 0 
            ? sessionTimes.reduce((sum, time) => sum + time, 0) / sessionTimes.length
            : 0;
          
          const avgMinutes = Math.round(avgTime / (1000 * 60));
          const avgTimeString = avgMinutes < 60 ? `${avgMinutes}m` : `${Math.round(avgMinutes/60)}h`;

          const sessionStats: SessionStats = {
            activeUsers: activeSessions.length,
            totalSessions: totalSessions.length,
            averageSessionTime: avgTimeString,
            mobileUsers: mobileCount,
            lastUpdated: new Date().toISOString()
          };

          setStats(sessionStats);
          setError(null);
        }
      } catch (err) {
        productionLogger.error('Failed to fetch session stats:', err);
        if (mounted) {
          if (err instanceof Error && err.message.includes('Rate limit exceeded')) {
            setError('Rate limit exceeded - please wait before refreshing');
          } else if (err instanceof Error && err.message.includes('Rate limit check failed')) {
            setError('Security check failed - contact administrator');
          } else {
            setError('Unable to load session data');
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSessionStats();

    // Enhanced Security: Set up real-time subscription with RLS filtering
    const channel = supabase
      .channel('session_stats_widget_enhanced', {
        config: {
          broadcast: { self: true },
          presence: { key: 'session_admin' }
        }
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_sessions'
        },
        async (payload) => {
          productionLogger.info('Session stats monitoring update:', payload);
          // Enhanced: Add debounced refresh to prevent rate limit issues
          setTimeout(() => {
            if (mounted) {
              fetchSessionStats();
            }
          }, 2000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_sessions'
        },
        async (payload) => {
          productionLogger.info('Session stats update:', payload);
          // Enhanced: Debounced refresh for session updates
          setTimeout(() => {
            if (mounted) {
              fetchSessionStats();
            }
          }, 2000);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          productionLogger.info('Session stats widget real-time subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          productionLogger.error('Session stats widget subscription error');
        }
      });

    // Enhanced Security: Increased refresh to 60 seconds to respect rate limits
    const interval = setInterval(fetchSessionStats, 60000);

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Active Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4 text-destructive" />
            Active Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Active Users
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <div className="text-2xl font-bold text-primary">
              {stats?.activeUsers || 0}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Eye className="w-3 h-3" />
              Live
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Online now (30min window)
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {stats?.averageSessionTime} avg
            </div>
            <div className="flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              {stats?.mobileUsers} mobile
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}